import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import Modal from "components/ui/modals";
import {
  Bars3Icon,
  CalendarIcon,
  TagIcon,
  BellIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import SetFinancialYear from "components/financialYear/SetFinancialYear";
import SetAgttp from "components/agttp/SetAgttp";
import useGeneralStore from "store/general";
import useFinancialYearStore from "store/financialYear";
import useAuth from "hooks/useAuth";
import { userNavigation } from "utils/navigation";
import { formatDateAndTime } from "utils/date";
import { useBellNotifications, useMarkBellSeen } from "http-hooks/messages/useMessages";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function TopNavbar({ setSidebarOpen }) {
  const [isSetModalOpen, setIsSetModalOpen] = useState(false);
  const [isSetAgttpModalOpen, setIsSetAgttpModalOpen] = useState(false);
  const { sidebarCollapsed, toggleSidebar } = useGeneralStore();
  const { currentFinancialYear } = useFinancialYearStore();
  const { logout, companyName, username, getUserTypeLabel } = useAuth();
  const navigate = useNavigate();

  const { data: bellData } = useBellNotifications();
  const { mutateAsync: markSeen } = useMarkBellSeen();

  const unreadCount = bellData?.unreadCount || 0;
  const bellItems = bellData?.items || [];

  const handleBellOpen = () => {
    if (unreadCount > 0) markSeen();
  };
  return (
    <>
      {isSetModalOpen && (
        <Modal
          open={isSetModalOpen}
          setOpen={(val) => setIsSetModalOpen(val)}
          maxWidth="sm"
        >
          <SetFinancialYear onClose={() => setIsSetModalOpen(false)} />
        </Modal>
      )}

      {isSetAgttpModalOpen && (
        <Modal
          open={isSetAgttpModalOpen}
          setOpen={(val) => setIsSetAgttpModalOpen(val)}
          maxWidth="sm"
        >
          <SetAgttp onClose={() => setIsSetAgttpModalOpen(false)} />
        </Modal>
      )}

      <div className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-x-2 md:gap-x-3 lg:gap-x-4 border-b border-gray-200 bg-white px-2 md:px-3 lg:px-4 shadow-sm">
        <button
          type="button"
          className="-m-1.5 p-1.5 text-gray-700 lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true" />
        </button>

        {/* Collapse button for desktop - visible only on large screens */}
        <button
          type="button"
          className="hidden lg:block -m-2.5 p-2.5 text-gray-700 hover:text-gray-900"
          onClick={toggleSidebar}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <span className="sr-only">{sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>

        {/* Separator */}
        <div
          className="h-4 md:h-5 lg:h-6 w-px bg-gray-900/10 lg:hidden"
          aria-hidden="true"
        />

        <div className="flex flex-1 gap-x-2 md:gap-x-3 lg:gap-x-4 self-stretch items-center">
          {/* Company Name Display */}
          <div className="flex items-center flex-1 min-w-0">
            <h1 className="text-xs md:text-sm lg:text-lg font-semibold leading-tight text-gray-900 break-words line-clamp-2">
              {companyName || "Company Name"}
            </h1>
          </div>
          
          <div className="flex items-center gap-x-2 md:gap-x-3 lg:gap-x-4 flex-shrink-0">
            {/* Version Display with First Letter Badge */}
            <span
              className="text-xs md:text-sm font-semibold leading-tight text-gray-900 cursor-pointer flex items-center gap-x-1"
              onClick={() => setIsSetAgttpModalOpen(true)}
            >
              <TagIcon className="h-3 w-3 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4" aria-hidden="true" />
              {/* First Letter Badge - Only visible on mobile when full label is hidden */}
              <span className="text-xs md:text-sm font-bold sm:hidden">
                {getUserTypeLabel().charAt(0).toUpperCase()}
              </span>
              {/* Full Label - Hidden on small screens, visible on larger */}
              <span className="hidden sm:inline">{getUserTypeLabel()}</span>
            </span>

            {/* Financial Year Display */}
            <span
              className="text-xs md:text-sm font-semibold leading-tight text-gray-900 cursor-pointer flex items-center gap-x-1"
              aria-hidden="true"
              onClick={() => setIsSetModalOpen(true)}
            >
              <CalendarIcon className="h-3 w-3 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4" aria-hidden="true" />
              {currentFinancialYear ? (
                <>
                  {/* Mobile: Show only dates without "FY" prefix */}
                  <span className="inline sm:hidden">
                    {formatDateAndTime(currentFinancialYear.fys, "YY")}-{formatDateAndTime(currentFinancialYear.fye, "YY")}
                  </span>
                  {/* Desktop: Show full with "FY" prefix */}
                  <span className="hidden sm:inline">
                    FY {formatDateAndTime(currentFinancialYear.fys, "YY")}-{formatDateAndTime(currentFinancialYear.fye, "YY")}
                  </span>
                </>
              ) : (
                <span>--</span>
              )}
            </span>

            {/* Separator */}
            <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" aria-hidden="true" />

            {/* Compose Message */}
            <button
              type="button"
              onClick={() => navigate("/messages/compose")}
              title="Compose Message"
              className="-m-1 p-1 text-gray-500 hover:text-gray-700"
            >
              <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            {/* Bell / Notifications */}
            <Menu as="div" className="relative">
              <Menu.Button
                className="-m-1 p-1 text-gray-500 hover:text-gray-700 relative"
                onClick={handleBellOpen}
              >
                <BellIcon className="h-5 w-5" aria-hidden="true" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-20 mt-2 w-80 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                    <span className="text-sm font-semibold text-gray-900">Notifications</span>
                    <button
                      onClick={() => navigate("/messages")}
                      className="text-xs text-indigo-600 hover:text-indigo-500"
                    >
                      View all
                    </button>
                  </div>
                  {bellItems.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-500 italic">No new notifications</p>
                  ) : (
                    bellItems.map((item) => (
                      <Menu.Item key={item.id}>
                        {({ active }) => (
                          <button
                            onClick={() => navigate(`/messages/${item.comment_id}`)}
                            className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 ${active ? "bg-gray-50" : ""}`}
                          >
                            <p className="text-xs font-semibold text-gray-800">{item.senderName}</p>
                            <p className="text-xs text-gray-500 truncate">{item.subject}</p>
                            <p className="text-xs text-gray-600 truncate mt-0.5">{item.comment_text}</p>
                          </button>
                        )}
                      </Menu.Item>
                    ))
                  )}
                </Menu.Items>
              </Transition>
            </Menu>

            {/* Separator */}
            <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" aria-hidden="true" />

            {/* Profile dropdown */}
            <Menu as="div" className="relative">
              <Menu.Button className="-m-1 md:-m-1.5 flex items-center p-1 md:p-1.5">
                <span className="sr-only">Open user menu</span>
                <img
                  className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 rounded-full bg-gray-50"
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(username || 'User')}&rounded=true`}
                  alt={username || 'User'}
                />
                <span className="hidden lg:flex lg:items-center">
                  <span
                    className="ml-4 text-sm font-semibold leading-6 text-gray-900"
                    aria-hidden="true"
                  >
                    {username || "User"}
                  </span>
                  <ChevronDownIcon
                    className="ml-2 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <a
                          href={item.href}
                          className={classNames(
                            active ? "bg-gray-50" : "",
                            "block px-3 py-1 text-sm leading-6 text-gray-900"
                          )}
                          onClick={
                            item.name === "Sign out"
                              ? logout
                              : undefined
                          }
                        >
                          {item.name}
                        </a>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </>
  );
} 